import React from 'react';
import { createUseStyles } from 'react-jss';
import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';

import { Theme } from '@app/theme';
import {
  Chip,
  LabeledIconChip,
  LabeledSection,
  Lottie,
  PublicBackgroundBlock,
} from '@app/core';
import { AppointmentInfoLine } from './AppointmentInfoLine';

import animationData from '@app/illu/Lottie/check.json';
import {
  ModeratedTaskFormat,
  Participation,
  ParticipationStatus,
  StudyType,
  useTask,
  useTaskMeeting,
} from '@app/api';
import QueryString from 'qs';
import { SummaryDevices } from './SummaryDevices';
import { AppointmentMeetingLink } from './AppointmentMeetingLink';
import { CancelParticipationBlock } from './CancelParticipationBlock';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 5,
    position: 'relative',
    zIndex: 1,

    width: '100%',
    maxWidth: theme.maxContentWidth.screen,

    margin: 'auto',
    [theme.media.mobile]: {
      boxShadow: 'none',
      maxWidth: '100%',
      gap: theme.marginBase * 7,
      padding: [[theme.marginBase * 3, theme.marginBase * 2]],
    },
  },
  title: {
    ...theme.fonts.h3,
    margin: 0,
    marginBottom: theme.marginBase,
  },
  successTitle: {
    marginBottom: 0,
  },
  animation: {
    margin: 'auto',
    width: theme.marginBase * 40,
    height: theme.marginBase * 40,
  },
  instructionContainer: {
    padding: theme.marginBase * 2,
    width: '100%',
  },
  instructionContent: {
    fontWeight: 500,
    whiteSpace: 'pre-wrap',
  },
}));

interface Props {
  participation: Participation;
}

export const AppointmentSummary = ({ participation }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { session } = participation;
  const { data: task } = useTask(participation.taskId);

  const { data: meeting } = useTaskMeeting(participation.taskId);

  const format = meeting?.format;
  const isPhysical = format === ModeratedTaskFormat.PHYSICAL_MEETING;

  const isUnModerated = session?.studyType === StudyType.UNMODERATED;

  const params = QueryString.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });
  const isDeviceInvalid = !!params.error && params.error === 'INVALID_DEVICE';
  const instructions = meeting?.instructions;

  return (
    <div className={classes.container}>
      <PublicBackgroundBlock>
        <h1
          className={classnames(classes.title, {
            [classes.successTitle]: params.success,
          })}
        >
          {params.success
            ? t('booking.success')
            : session?.studyType === StudyType.MODERATED
            ? t('booked.summary.title')
            : t('booked.summary.titleUnModerated')}
        </h1>
        {params.success && (
          <Lottie
            animationData={animationData}
            play
            loop={false}
            className={classes.animation}
          />
        )}
        <AppointmentInfoLine participation={participation} />
        {task?.sessionPhoneNumber && (
          <LabeledIconChip
            label={t('booked.summary.ownerPhoneNumber')}
            chipLabel={task?.sessionPhoneNumber}
            chipIcon="ri-phone-line"
            labelColor="inkSecondary"
          />
        )}
        <AppointmentMeetingLink
          participation={participation}
          isDeviceInvalid={isDeviceInvalid}
        />
        {!!meeting?.address && isPhysical && (
          <LabeledIconChip
            label={t('booked.summary.instructions')}
            chipLabel={meeting?.address}
            chipIcon="ri-information-line"
            labelColor="inkSecondary"
          />
        )}
        {!!instructions && (
          <LabeledSection label={t('booked.summary.instructions')}>
            <Chip className={classes.instructionContainer}>
              <p className={classes.instructionContent}>{instructions}</p>
            </Chip>
          </LabeledSection>
        )}
        {session && (
          <SummaryDevices session={session} participation={participation} />
        )}
      </PublicBackgroundBlock>
      <CancelParticipationBlock
        hidden={
          !!params.success || isUnModerated
            ? ![
                ParticipationStatus.IN_PROGRESS,
                ParticipationStatus.ACCEPTED,
              ].includes(participation.status)
            : participation.status !== ParticipationStatus.BOOKED
        }
      />
    </div>
  );
};
